import React, { useState, useEffect } from 'react';
import { restaurantManagerAPI } from '../../services/api';

// Outlet picker for a restaurant manager. Selecting an outlet mints an
// owner-scoped session token and enters the normal restaurant dashboard for it.
function RestaurantManagerSelect() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Self-guard: this page must run under the MANAGER identity. When we arrive
    // here via browser-BACK from an opened outlet, the active token is still the
    // owner-scoped session — restore the manager token so the picker works (and
    // so we're no longer "inside" an outlet). Runs on mount AND on `pageshow`
    // (which also fires when the page is restored from the back/forward cache).
    const init = async () => {
      const managerToken = localStorage.getItem('manager_token');
      if (!managerToken) {
        window.location.replace('/restaurant-manager/login');
        return;
      }
      if (localStorage.getItem('token') !== managerToken) {
        localStorage.setItem('token', managerToken);
      }
      localStorage.removeItem('managing_restaurant');
      setLoading(true);
      try {
        const res = await restaurantManagerAPI.getMyRestaurants();
        setRestaurants(res.data.data.restaurants || []);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load your outlets');
      } finally {
        setLoading(false);
      }
    };

    init();
    const onPageShow = (e) => { if (e.persisted) init(); };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const openOutlet = async (r) => {
    setOpening(r._id);
    setError('');
    try {
      const res = await restaurantManagerAPI.createSession(r._id);
      const { token, restaurant } = res.data.data;
      localStorage.setItem('token', token); // owner-scoped session for this outlet
      localStorage.setItem('managing_restaurant', JSON.stringify({ id: restaurant._id, name: restaurant.name }));
      window.location.href = '/restaurant/dashboard';
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open this outlet');
      setOpening(null);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('manager_token');
    localStorage.removeItem('managing_restaurant');
    localStorage.removeItem('refreshToken');
    window.location.href = '/restaurant-manager/login';
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', padding: '24px 16px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, color: '#0F172A' }}>🏬 Your Outlets</h2>
            <p style={{ margin: '4px 0 0', color: '#64748B', fontSize: 14 }}>Pick an outlet to manage its dashboard</p>
          </div>
          <button onClick={logout} style={{ marginLeft: 'auto', padding: '8px 16px', border: '1px solid #E2E8F0', borderRadius: 8, background: 'white', color: '#64748B', fontWeight: 600, cursor: 'pointer' }}>Logout</button>
        </div>

        {error && <div style={{ color: '#DC2626', fontSize: 14, marginBottom: 12 }}>{error}</div>}

        {loading ? (
          <div style={{ color: '#64748B' }}>Loading…</div>
        ) : restaurants.length === 0 ? (
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 40, textAlign: 'center', color: '#64748B' }}>
            No outlets are assigned to you yet. Please contact the admin.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {restaurants.map((r) => (
              <button key={r._id} onClick={() => openOutlet(r)} disabled={!!opening}
                style={{ textAlign: 'left', background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, cursor: opening ? 'wait' : 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 10, background: '#F1F5F9', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {r.primaryImage ? <img src={r.primaryImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 22 }}>🍽️</span>}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: '#64748B' }}>{r.address?.city || ''}</div>
                    <div style={{ fontSize: 11, marginTop: 4, color: r.isOpen ? '#059669' : '#DC2626', fontWeight: 600 }}>{r.isOpen ? '● Open' : '● Closed'}</div>
                  </div>
                </div>
                <div style={{ marginTop: 12, textAlign: 'right', color: '#FF6961', fontWeight: 700, fontSize: 13 }}>
                  {opening === r._id ? 'Opening…' : 'Open dashboard →'}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default RestaurantManagerSelect;
